import axios from 'axios';
import { API_URL } from '../config/apiConfig';


const extractFilenameFromContentDisposition = (contentDisposition, fallback = 'invoice.pdf') => {
    if (!contentDisposition) return fallback;

    const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FilenameMatch && utf8FilenameMatch[1]) {
        try {
            return decodeURIComponent(utf8FilenameMatch[1]);
        } catch (_) {
            return utf8FilenameMatch[1];
        }
    }

    const quotedFilenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (quotedFilenameMatch && quotedFilenameMatch[1]) {
        return quotedFilenameMatch[1];
    }

    const plainFilenameMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (plainFilenameMatch && plainFilenameMatch[1]) {
        return plainFilenameMatch[1].trim();
    }

    return 'invoice.pdf';
};

const triggerBlobDownload = (blob, filename, preopenedTab = null) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    if (preopenedTab && !preopenedTab.closed) {
        preopenedTab.location.href = url;
    } else {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    setTimeout(() => {
        window.URL.revokeObjectURL(url);
    }, 10000);
};

const parseBlobError = async (error, fallbackMessage) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const json = JSON.parse(text);
            return new Error(json.error || fallbackMessage);
        } catch (_) {
            return new Error(fallbackMessage);
        }
    }
    return error instanceof Error ? error : new Error(fallbackMessage);
};

export const downloadInvoice = async (transactionId, options = {}) => {
    const { preopenedTab = null } = options;
    try {
        const token = localStorage.getItem('token');

        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await axios.get(`${API_URL}/invoices/download/${transactionId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            responseType: 'blob'
        });

        const contentDisposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'] || 'application/pdf';
        const filename = extractFilenameFromContentDisposition(contentDisposition);
        const blob = new Blob([response.data], { type: contentType });
        triggerBlobDownload(blob, filename, preopenedTab);

        return { success: true };
    } catch (error) {
        console.error('❌ Error downloading invoice:', error);
        throw await parseBlobError(error, 'Failed to download invoice. Please try again.');
    }
};

export const downloadInvoicesZip = async (payload, options = {}) => {
    const { preopenedTab = null } = options;
    try {
        const token = localStorage.getItem('token');

        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await axios.post(`${API_URL}/invoices/download-zip`, payload, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            responseType: 'blob'
        });

        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
            const text = await response.data.text();
            const json = JSON.parse(text);
            throw new Error(json.error || 'Failed to download invoices zip.');
        }

        const contentDisposition = response.headers['content-disposition'];
        const filename = extractFilenameFromContentDisposition(contentDisposition, 'invoices.zip');
        const blob = new Blob([response.data], { type: 'application/zip' });
        triggerBlobDownload(blob, filename, preopenedTab);

        return { success: true };
    } catch (error) {
        console.error('❌ Error downloading invoices zip:', error);
        throw await parseBlobError(error, 'Failed to download invoices zip. Please try again.');
    }
};

export const deleteTransaction = async (transactionId) => {
    try {
        const token = localStorage.getItem('token');

        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await axios.delete(`${API_URL}/invoices/transaction/${transactionId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            }
        });

        return response.data;
    } catch (error) {
        console.error('❌ Error deleting transaction:', error);
        throw error;
    }
}